import { NgClass } from '@angular/common';
import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import {
    AppSku,
    VehicleType,
} from '@polaris-customerweb/sharedcomponents.core';
import {
    AddVehicleComponent,
    AddVehicleCtaComponent,
    AddVehicleDialogResponse,
    Equipment,
    Garageable,
    GarageItemsService,
    Vehicle,
    VehicleMultiGridComponent,
} from '@polaris-customerweb/sharedcomponents.garage';
import { LogService } from '@polaris-customerweb/sharedcomponents.shared';
import {
    ConnectedVehicleService,
    RideCommandPlusComponent,
    SubscriptionStatusClickValue,
    VehicleDetailComponent,
    VehicleHealthDashboardComponent,
    VehiclePermissionService,
} from '@polaris-customerweb/sharedcomponents.vehicle-health';
import { attempt, orderBy } from 'lodash-es';
import { combineLatest, Observable } from 'rxjs';
import { filter, switchMap, take, takeWhile } from 'rxjs/operators';
import { GeoFileImportComponent } from 'src/app/components/shared/components/geo-file-import/geo-file-import.component';
import { AppState } from 'src/app/models/app-state';
import { SubscriptionManagementService } from 'src/app/services/subscription-management.service';
import { AnalyticsService } from 'src/app/state/analytics/analytics.service';
import { WINDOW } from 'src/app/state/app-config/factories/window.factory';
import { GarageItemsBrandRoutingService } from 'src/app/state/garage/garage-items-brand-routing.service';
import {
    GetGarageItems,
    SetActiveGarageItemId,
} from 'src/app/state/garage/garage.actions';
import {
    selectActiveGarageItem,
    selectGarageItems,
    selectGarageItemsLoaded,
} from 'src/app/state/garage/garage.selectors';
import { selectVehiclesForUser } from 'src/app/state/vehicle';
import { CtaComponent } from '../cta/cta.component';
import { HomeCommunityComponent } from '../home-community/home-community.component';
import { HomeHelpfulLinksComponent } from '../home-helpful-links/home-helpful-links.component';
import { HomeRidesListComponent } from '../home-rides-list/home-rides-list.component';

@Component({
    imports: [
        AddVehicleCtaComponent,
        CtaComponent,
        HomeCommunityComponent,
        HomeHelpfulLinksComponent,
        HomeRidesListComponent,
        NgClass,
        RideCommandPlusComponent,
        VehicleDetailComponent,
        VehicleHealthDashboardComponent,
        VehicleMultiGridComponent,
    ],
    selector: 'rdx-home-authenticated',
    styleUrls: ['./home-authenticated.component.scss'],
    templateUrl: './home-authenticated.component.html',
})
export class HomeAuthenticatedComponent implements OnDestroy, OnInit {
    @Input() vehicleType: VehicleType = 'ORV';

    @Output() vehicleClicked = new EventEmitter<Vehicle>();

    readonly className = 'HomeAuthenticatedComponent';
    allItems: (Vehicle | Equipment)[] = [];
    rcPlusIds: string[] = [];
    showRcPlus = false;
    equipmentId: string;
    expandTelematics = false;
    garageItemsLoaded = false;
    hasLocationPermission$: Observable<boolean>;
    hasViewTelemetryPermission$: Observable<boolean>;
    isVehicleDetailLoading: boolean;
    otherVehicles: Vehicle[] = [];
    otherVehiclesTabActive = true;
    selectedGarageItem: Garageable | null = null;
    selectedVehicle: Vehicle | null = null;
    vehicleId: string;
    vehicles: (Vehicle | Equipment)[] = [];
    appSku: AppSku;

    private destructors: (() => void)[] = [];
    private isDestroyed = false;

    constructor(
        private readonly activatedRoute: ActivatedRoute,
        private readonly analyticsService: AnalyticsService,
        private readonly configService: ConfigService,
        private readonly connectedVehicleService: ConnectedVehicleService,
        private readonly garageItemsBrandRoutingService: GarageItemsBrandRoutingService,
        private readonly garageItemsService: GarageItemsService,
        private readonly logService: LogService,
        private readonly matDialog: MatDialog,
        private readonly router: Router,
        private readonly store: Store<AppState>,
        private readonly subscriptionManagementService: SubscriptionManagementService,
        private readonly vehiclePermissionService: VehiclePermissionService,
        @Inject(WINDOW) private readonly window: Window
    ) {}

    get hasVehicle(): boolean {
        return this.vehicles.length > 0;
    }

    setVehicleDetailLoading(loading: boolean): void {
        this.isVehicleDetailLoading = loading;
    }

    ngOnInit(): void {
        this.appSku = this.configService.appSku;
        this.store.dispatch(GetGarageItems());
        this.activatedRoute.queryParamMap
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe((params) => {
                const vehicleId = params.get('vehicleId') || '';
                this.vehicleId = params.get('vehicleId') || '';
                this.equipmentId = params.get('equipmentId') || '';
                const showTelemetry = params.get('showTelemetry');
                this.store.dispatch(
                    SetActiveGarageItemId({
                        garageItemId: vehicleId,
                    })
                );

                if (this.vehicleId) {
                    this.garageItemsService.$activeGarageItemId.set(
                        this.vehicleId
                    );
                }

                if (showTelemetry === 'true') {
                    this.expandTelematics = true;
                }
            });

        this.connectedVehicleService.triggerImmediateActiveAlertPoll();

        if (this.configService.featureFlags.areLocationAlertsEnabled) {
            this.selectActiveAlertVehicle();
        }

        combineLatest([
            this.store.select(selectGarageItems),
            this.store.select(selectGarageItemsLoaded),
        ])
            .pipe(
                filter(([, loaded]) => loaded),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(([items]) => {
                items = orderBy(
                    items,
                    (item) => item.updated ?? new Date(0),
                    'desc'
                );
                const onBrandVehicles: (Vehicle | Equipment)[] = [];
                const offBrandVehicles: (Vehicle | Equipment)[] = [];
                items.forEach((item) => {
                    if (item.isOnBrand?.(this.configService.appSku)) {
                        onBrandVehicles.push(item);
                    } else {
                        offBrandVehicles.push(item);
                    }
                });
                this.vehicles = [...new Set(onBrandVehicles)];
                this.otherVehicles = [
                    ...new Set(offBrandVehicles as Vehicle[]),
                ];

                if (
                    this.vehicles.length === 1 &&
                    this.otherVehicles.length === 0
                ) {
                    const activeVehicleId = this.vehicles[0]?.id;
                    this.store.dispatch(
                        SetActiveGarageItemId({
                            garageItemId: activeVehicleId,
                        })
                    );
                    this.garageItemsService.$activeGarageItemId.set(
                        activeVehicleId
                    );
                    if (this.vehicles[0] instanceof Vehicle) {
                        this.getPermissions(this.vehicles[0]);
                        this.rcPlusIds = [this.vehicles[0].id];
                    }
                }
                this.garageItemsLoaded = true;
                this.allItems = [...this.vehicles, ...this.otherVehicles];
                this.rcPlusIds = this.rcPlusIds.length
                    ? this.rcPlusIds
                    : this.allItems
                          .filter(
                              (x) =>
                                  x instanceof Vehicle &&
                                  x.isOnBrand(this.appSku)
                          )
                          .map((x) => (x as Vehicle).id);
            });

        const activeGarageItems$ = combineLatest([
            this.store.select(selectActiveGarageItem),
            this.store.select(selectGarageItemsLoaded),
        ]).pipe(
            filter(([, loaded]) => loaded),
            takeWhile(() => !this.isDestroyed)
        );

        activeGarageItems$.subscribe(([activeGarageItem]) => {
            const isOnBrandVehicle = this.vehicles.some(
                (vehicle) => vehicle.id === activeGarageItem?.id
            );

            if (isOnBrandVehicle) {
                if (activeGarageItem instanceof Vehicle) {
                    this.getPermissions(activeGarageItem);
                    this.selectedVehicle = activeGarageItem;
                    this.isVehicleDetailLoading = true;
                }
                this.selectedGarageItem = activeGarageItem;
            } else {
                this.selectedVehicle = null;
                this.selectedGarageItem = null;
            }
            this.rcPlusIds =
                this.selectedGarageItem &&
                this.selectedGarageItem instanceof Vehicle
                    ? [this.selectedGarageItem.id]
                    : this.allItems
                          .filter(
                              (x) =>
                                  x instanceof Vehicle &&
                                  x.isOnBrand(this.appSku)
                          )
                          .map((x) => (x as Vehicle).id);
            this.otherVehiclesTabActive = !this.selectedGarageItem;
        });
    }

    ngOnDestroy(): void {
        this.destructors.forEach(attempt);
        this.store.dispatch(SetActiveGarageItemId({ garageItemId: '' }));
        this.garageItemsService.$activeGarageItemId.set('');
        this.isDestroyed = true;
    }

    showAddBikeForm(): void {
        this.analyticsService.fireEvent('add bike form clicked');
        this.matDialog
            .open<
                AddVehicleComponent,
                { initialState: string; vehicleId: string | undefined },
                AddVehicleDialogResponse
            >(AddVehicleComponent, {
                data: { initialState: 'start', vehicleId: undefined },
            })
            .afterClosed()
            .subscribe((res) => {
                if (res?.addedVehicle) {
                    void this.router.navigate(['maintenance']);
                }
            });
    }

    onVehicleClicked(vehicle: Garageable | undefined): void {
        if (vehicle) {
            if (vehicle.isOnBrand?.(this.configService.appSku)) {
                void this.router.navigate(['home'], {
                    queryParams: { vehicleId: vehicle.id },
                });
            } else {
                this.redirectToBrand(vehicle);
                return;
            }
        } else {
            void this.router.navigate(['home']);
        }
        this.store.dispatch(
            SetActiveGarageItemId({ garageItemId: vehicle?.id || '' })
        );
        this.garageItemsService.$activeGarageItemId.set(vehicle?.id ?? '');
        this.expandTelematics = false;
        if (vehicle?.isVehicle) {
            this.getPermissions(vehicle as Vehicle);
        }

        if (vehicle) {
            this.analyticsService.fireEvent('vehicle selected');
        }
    }

    getPermissions(vehicle: Vehicle) {
        this.hasLocationPermission$ =
            this.vehiclePermissionService.canViewLocation(vehicle.vin);
        this.hasViewTelemetryPermission$ =
            this.vehiclePermissionService.canViewTelemetry(vehicle.vin);
    }

    importRide() {
        const dialogRef = this.matDialog.open(GeoFileImportComponent);
        const dialogSub = dialogRef.afterClosed().subscribe();
        this.destructors.push(() => dialogSub.unsubscribe.bind(this));
    }

    onDismissAllDtcs() {
        this.analyticsService.fireEvent('dismiss all diagnostic codes');
    }

    onTelematicsEvent(expand: boolean, widgetType?: string): void {
        this.expandTelematics = expand;
        if (expand) {
            this.analyticsService.fireEvent(
                'vehicle health widget clicked',
                widgetType
            );
        } else {
            this.analyticsService.fireEvent('close vehicle health');
        }
    }

    redirectToMap(vehicleId: string): void {
        this.analyticsService.fireEvent('Vehicle location clicked map');
        if (vehicleId) {
            const brandDomain = this.selectedVehicle
                ? this.garageItemsBrandRoutingService.getGarageItemBrandDomain(
                      this.selectedVehicle
                  )
                : '';

            if (brandDomain) {
                this.window.open(
                    `${brandDomain}map/vehicle/${vehicleId}`,
                    '_blank'
                );
            } else {
                this.router
                    .navigate(['map', 'vehicle', vehicleId])
                    .catch(() => {
                        this.logService.error(
                            this.className,
                            'failed to route to vehicle detail'
                        );
                    });
            }
        }
    }

    openSubscriptionManagement(values: {
        identifier: string;
        link: SubscriptionStatusClickValue;
        vin: string;
    }): void {
        this.analyticsService.fireEvent('subscription management clicked');
        this.subscriptionManagementService
            .manageSubscription(values.vin, values.identifier)
            .subscribe();
    }

    viewAllCodes(): void {
        this.analyticsService.fireEvent('click view diagnostic code list');
        if (this.selectedGarageItem?.id) {
            void this.router.navigate([
                'maintenance',
                'vehicle',
                this.selectedGarageItem.id,
                'diagnostics',
            ]);
        }
    }

    viewCodeHistory(): void {
        this.analyticsService.fireEvent('click view diagnostic code history');
        if (this.selectedGarageItem?.id) {
            void this.router.navigate(
                [
                    'maintenance',
                    'vehicle',
                    this.selectedGarageItem.id,
                    'diagnostics',
                ],
                {
                    queryParams: {
                        dtcView: 'history',
                    },
                }
            );
        }
    }

    viewMaintenanceSchedule(): void {
        if (this.selectedGarageItem?.id) {
            void this.router.navigate([
                '/maintenance',
                'vehicle',
                this.selectedGarageItem.id,
                'maintenance-schedule',
            ]);
        }
    }

    widgetClick(): void {
        this.analyticsService.fireEvent('expand diagnostic code widget');
    }

    ignitionSecurityClick(): void {
        this.analyticsService.fireEvent('ignition security clicked');
    }

    ignitionSecurityStatusUpdate(): void {
        this.analyticsService.fireEvent('ignition security status updated');
    }

    appLinkClick(link: string): void {
        this.analyticsService.fireEvent(`redirected to ${link}`);
    }

    bannerClick(bannerType: string) {
        this.analyticsService.fireEvent(`${bannerType} banner clicked`);
    }

    vehicleWakeClick() {
        this.analyticsService.fireEvent('vehicle wake clicked');
    }

    private selectActiveAlertVehicle() {
        this.store
            .select(selectVehiclesForUser)
            .pipe(
                switchMap(() =>
                    this.connectedVehicleService.getVehicleWithActiveAlert()
                ),
                take(1),
                filter((vehicle) => Boolean(vehicle))
            )
            .subscribe((vehicle: Vehicle) => {
                this.router
                    .navigate(['home'], {
                        queryParams: { vehicleId: vehicle.id },
                    })
                    .catch(() => {
                        this.logService.error(
                            this.className,
                            'navigate to active vehicle failed'
                        );
                    });
                this.store.dispatch(
                    SetActiveGarageItemId({
                        garageItemId: vehicle.id,
                    })
                );
                this.garageItemsService.$activeGarageItemId.set(vehicle.id);
            });
    }

    private redirectToBrand(vehicle: Garageable) {
        const domain =
            this.garageItemsBrandRoutingService.getGarageItemBrandDomain(
                vehicle
            );
        if (domain) {
            if (vehicle.classCode) {
                this.window.open(`${domain}home?vehicleId=${vehicle.id}`);
            } else {
                this.window.open(`${domain}maintenance/vehicle/${vehicle.id}`);
            }
        } else {
            void this.router.navigate(['maintenance', 'vehicle', vehicle.id]);
        }
    }
}
